/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useContext, useState, useEffect, useRef} from 'react'
import {useLocation} from 'react-router-dom'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    Image,
    Button,
    Flex,
    Text,
    Modal,
    ModalHeader,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalOverlay,
    Stack,
    useBreakpointValue,
    useMultiStyleConfig,
    HStack,
    VStack,
    Box,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import UniversalFeedbackRecommended from '../components/recommended-products/universal-feedback-recommended'

import {CheckIcon, CloseIcon} from '../components/icons'
import OmnibusModalPrice from '../components/display-price/omnibus/omnibus-modal-price'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import DisplayPrice from '../components/display-price'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useToast} from './use-toast'
import {remCalc} from '../theme/_utils'
import {useBasketTotalItems} from './use-basket-total-items'
import {findImageGroupBy} from '../utils/image-groups-utils'
import {BRAND_KEY} from '../brand'
import WarrantySelector from '../components/recommended-products/warranty-selector'
import AddWarrantyToCartButton from '../components/recommended-products/partials/add-warranty-to-cart-button'

/**
 * This is the context for managing the AddToCartModal.
 * Used in top level App component.
 */
export const AddToCartModalContext = React.createContext()

export const useAddToCartModalContext = () => useContext(AddToCartModalContext)

export const AddToCartModalProvider = ({children}) => {
    const addToCartModal = useAddToCartModal()
    return (
        <AddToCartModalContext.Provider value={addToCartModal}>
            {children}
            <AddToCartModal />
        </AddToCartModalContext.Provider>
    )
}
AddToCartModalProvider.propTypes = {
    children: PropTypes.node.isRequired,
}

/**
 * Visual feedback (a modal) for adding item to the cart.
 */
export const AddToCartModal = () => {
    const {isOpen, onClose, data} = useAddToCartModalContext()
    const {itemsAdded = [], isBundle, isSubscription, officinaShapshot} = data || {}
    const {currency} = useCurrency()
    const showToast = useToast()
    const isBraun = BRAND_KEY === 'braun'

    const intl = useIntl()
    const styles = useMultiStyleConfig('Modal')

    const {data: basket} = useCurrentBasket()
    const totalItems = useBasketTotalItems()
    const size = useBreakpointValue({base: 'full', lg: '2xl', xl: '4xl'})
    const [selectedWarrantyVariantId, setSelectedWarrantyVariantId] = useState(null)

    // we need a ref here to persist the recommendations when you add a new product from inside the modal
    const recommendations = useRef(null)

    // if the modal is closed, we reset the recommendations, tried added it to the onClose of the modal
    // but when closed and with null recommendations, it rerenders and it reset to the previous ones from the basket
    if (!isOpen) {
        recommendations.current = null
    }

    // for some pages we removed the recommendations inside the product in order to optimize the performance (for example plp)
    // so we get them from the basket.
    if (basket?.c_additionalData?.recommendations && !recommendations.current) {
        recommendations.current = basket?.c_additionalData?.recommendations
    }

    if (isSubscription && itemsAdded.length > 0) {
        recommendations.current = itemsAdded[0].product?.c_recommendations.filter((rec) => {
            return rec?.recommendationType?.value === 4
        })
    }
    const getSelectedWarrantyProduct = () => {
        if (!selectedWarrantyVariantId) return null

        for (const warranty of warranties) {
            const variant = warranty.variants.find((v) => v.productId === selectedWarrantyVariantId)
            if (variant) {
                return {
                    ...variant,
                    id: variant.productId,
                    variationAttributes: [],
                    variants: [variant],
                }
            }
        }
        return null
    }

    useEffect(() => {
        // the message is displayed if a custom product is added and there are other products in the cart
        if (isOpen && officinaShapshot && totalItems > 0) {
            showToast({
                title: intl.formatMessage({
                    defaultMessage:
                        'Since you add a customized product to the cart, the order cannot include other types of products. Therefore, the cart has been emptied of previous products.',
                    id: 'officina.success.cart_emptied',
                }),
                status: 'success',
            })
        }
    }, [isOpen])

    if (!isOpen) {
        return null
    }

    const hasRecommended =
        (recommendations.current || []).filter(
            (rec) => rec?.recommendationType?.value === 2 || rec?.recommendationType?.value === 4,
        ).length > 0

    const warranties = recommendations.current?.filter(
        (rec) => rec?.recommendationType?.value === 5,
    )
    const hasWarranties = warranties?.length > 0
    return (
        <Modal size={size} isOpen={isOpen} onClose={onClose} scrollBehavior="inside" isCentered>
            <ModalOverlay {...styles.modalOverlay} />
            <ModalContent
                containerProps={{
                    'data-testid': 'add-to-cart-modal',
                    paddingY: {md: remCalc(40), lg: remCalc(80)},
                }}
                {...{
                    ...styles.modalContent,
                    bottom: '0',
                    position: {base: 'fixed', md: '	static'},
                }}
                sx={{
                    maxHeight: {base: `calc(100vh - ${remCalc(16)})`, md: '100%'},
                    '@supports (height: 100dvh)': {
                        maxHeight: {base: `calc(100dvh - ${remCalc(16)})`, md: '100%'},
                    },
                }}
            >
                <ModalHeader {...styles.modalHeader}>
                    <Flex {...styles.modalHeaderCheck}>
                        {!isSubscription && <CheckIcon {...styles.checkIconBox} />}

                        <Text {...styles.modalHeaderTitle}>
                            {isSubscription
                                ? intl.formatMessage(
                                      {
                                          defaultMessage: 'Buy in subscription and <b>save</b>',
                                          id: 'add_to_cart_modal.label.recommendeed_subscriptions',
                                          b: (boldString) => <b>{boldString}</b>,
                                      },
                                      {quantity: totalItems},
                                  )
                                : intl.formatMessage(
                                      {
                                          defaultMessage: 'Added item to basket',
                                          id: 'add_to_cart_modal.header',
                                      },
                                      {quantity: totalItems},
                                  )}
                        </Text>
                    </Flex>
                    <CloseIcon
                        data-testid="close-modal"
                        onClick={onClose}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault()
                                onClose()
                            }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label={intl.formatMessage({
                            defaultMessage: 'Close Modal',
                            id: 'add_to_cart_modal.close_icon',
                        })}
                        {...styles.closeIcon}
                    />
                </ModalHeader>
                {!isSubscription && (
                    <ModalBody {...styles.modalBodyCart}>
                        <Stack {...styles.cartProduct}>
                            <Stack {...styles.cartProductTile}>
                                {itemsAdded.map((item, i) => {
                                    const {product, variant, quantity} = item

                                    let image

                                    if (product.c_isOfficina && officinaShapshot) {
                                        image = {
                                            url: officinaShapshot,
                                            alt: product.productName,
                                        }
                                    } else {
                                        image = item.isBundle
                                            ? {url: product.image, alt: product.productName}
                                            : product.c_image ||
                                              findImageGroupBy(product.imageGroups, {
                                                  viewType: 'small',
                                                  selectedVariationAttributes:
                                                      product?.variationValues ??
                                                      product.variationValues,
                                              })?.images?.[0]
                                    }

                                    return (
                                        <HStack
                                            {...styles.productStack}
                                            key={i}
                                            data-testid="product-added"
                                        >
                                            <Stack {...styles.productImageContainer}>
                                                {image && (
                                                    <Image
                                                        {...styles.productImage}
                                                        src={image.url || image.link}
                                                        alt={image.alt}
                                                    />
                                                )}
                                            </Stack>

                                            <Flex {...styles.productDetails}>
                                                <Stack {...styles.productHeaderContainer}>
                                                    <Text {...styles.productHeader}>
                                                        {variant?.c_variantIdentifier ||
                                                            product?.c_variantIdentifier ||
                                                            product?.productName ||
                                                            product?.name}
                                                    </Text>
                                                </Stack>

                                                <Stack {...styles.pricingStack}>
                                                    {isBundle ? (
                                                        product.price === 0 ? (
                                                            <Box {...styles.bundleFreeGiftLabel}>
                                                                <FormattedMessage
                                                                    defaultMessage="Free Gift"
                                                                    id="bundle_free_gift.text"
                                                                />
                                                            </Box>
                                                        ) : (
                                                            <DisplayPrice
                                                                priceData={{
                                                                    listPrice:
                                                                        product.standardPrice,
                                                                    currentPrice: product.price,
                                                                    isOnSale:
                                                                        product.price <
                                                                        product.standardPrice,
                                                                }}
                                                                currency={currency}
                                                                listPriceProps={{
                                                                    ...styles.bundleProductListPrice,
                                                                }}
                                                                currentPriceProps={{
                                                                    ...styles.bundleProductDetailPrice,
                                                                }}
                                                                priceContainerProps={{
                                                                    ...styles.bundleProductPriceContainer,
                                                                }}
                                                                discountProps={{display: 'none'}}
                                                                productDetailPriceView={true}
                                                            />
                                                        )
                                                    ) : (
                                                        <OmnibusModalPrice
                                                            product={product}
                                                            quantity={quantity}
                                                        />
                                                    )}
                                                </Stack>
                                            </Flex>
                                        </HStack>
                                    )
                                })}
                            </Stack>
                            {/* //WARRANTY SECTION  */}
                            {hasWarranties && (
                                <>
                                    {warranties.map((warrantyGroup) => (
                                        <WarrantySelector
                                            key={warrantyGroup.warrantyGroupId}
                                            warrantyGroup={warrantyGroup}
                                            parentProductId={itemsAdded?.[0]?.product?.id}
                                            selectedVariantId={selectedWarrantyVariantId}
                                            onVariantSelect={setSelectedWarrantyVariantId}
                                        />
                                    ))}
                                </>
                            )}
                        </Stack>
                        <>
                            <HStack
                                {...(hasRecommended ? styles.modalHeaderCartContainer : {})}
                                alignItems="baseline"
                            >
                                <Text {...styles.modalHeaderTitle} {...styles.boldText}>
                                    {totalItems === 1
                                        ? intl.formatMessage(
                                              {
                                                  defaultMessage: '{quantity} item',
                                                  id: 'add_to_cart_modal.info.added_to_cart',
                                              },
                                              {quantity: totalItems},
                                          )
                                        : intl.formatMessage(
                                              {
                                                  defaultMessage: '{quantity} items',
                                                  id: 'add_to_cart_modal.info.added_to_cart_items',
                                              },
                                              {quantity: totalItems},
                                          )}
                                    {''}
                                </Text>
                                <Text {...styles.modalHeaderTitle}>
                                    {intl.formatMessage({
                                        defaultMessage: 'in your basket',
                                        id: 'add_to_cart_modal.info.added_to_cart_message',
                                    })}
                                </Text>
                            </HStack>
                            <HStack
                                {...{
                                    ...styles.buttonsStack,
                                    display: {base: isBundle ? 'flex' : 'none', lg: 'flex'},
                                }}
                            >
                                <Button
                                    as={Link}
                                    {...styles.productButton}
                                    to="/cart"
                                    onClick={onClose}
                                    variant="primary"
                                >
                                    {intl.formatMessage({
                                        id: 'global.go-to-basket',
                                        defaultMessage: 'Go to basket',
                                    })}
                                </Button>
                                {hasWarranties ? (
                                    <AddWarrantyToCartButton
                                        product={getSelectedWarrantyProduct()}
                                        parentProductId={itemsAdded?.[0]?.product?.id}
                                        isDisabled={!selectedWarrantyVariantId}
                                        callback={() => {
                                            setSelectedWarrantyVariantId(null)
                                            onClose()
                                        }}
                                    />
                                ) : (
                                    <Button
                                        {...{
                                            ...styles.buttonsStack,
                                            display: {base: 'none', lg: 'flex'},
                                        }}
                                        onClick={onClose}
                                        variant="secondary"
                                    >
                                        {intl.formatMessage({
                                            defaultMessage: 'Continue shopping',
                                            id: 'add_to_cart_modal.link.compare',
                                        })}
                                    </Button>
                                )}
                            </HStack>
                        </>
                    </ModalBody>
                )}

                {/* If it's a bundle I hide the accessory products section */}
                {!isBundle && !hasWarranties && (
                    <ModalFooter {...styles.modalFooter}>
                        <UniversalFeedbackRecommended
                            isSubscription={isSubscription}
                            products={(recommendations.current || [])
                                .filter(
                                    (rec) =>
                                        rec?.recommendationType?.value === 2 ||
                                        rec?.recommendationType?.value === 4,
                                )
                                .map((rec) => rec.recommendedItemId)}
                        />
                        {!isSubscription && (
                            <VStack
                                backgroundColor="background.default"
                                {...styles.footerButtonStack}
                                {...styles.stickyButton}
                            >
                                <Button
                                    as={Link}
                                    {...styles.productButton}
                                    to="/cart"
                                    onClick={onClose}
                                    variant="primary"
                                >
                                    {intl.formatMessage({
                                        id: 'global.go-to-basket',
                                        defaultMessage: 'Go to basket',
                                    })}
                                </Button>
                                {!isBraun && (
                                    <Button
                                        {...styles.productButton}
                                        onClick={onClose}
                                        variant="secondary"
                                    >
                                        {intl.formatMessage({
                                            defaultMessage: 'Continue shopping',
                                            id: 'add_to_cart_modal.link.compare',
                                        })}
                                    </Button>
                                )}
                            </VStack>
                        )}
                    </ModalFooter>
                )}
            </ModalContent>
        </Modal>
    )
}

AddToCartModal.propTypes = {
    product: PropTypes.shape({
        name: PropTypes.string,
        imageGroups: PropTypes.array,
    }),
    variant: PropTypes.shape({
        productId: PropTypes.string,
        variationValues: PropTypes.object,
    }),
    quantity: PropTypes.number,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    children: PropTypes.any,
}

export const useAddToCartModal = () => {
    const [state, setState] = useState({
        isOpen: false,
        data: null,
    })

    const {pathname} = useLocation()
    useEffect(() => {
        if (state.isOpen) {
            setState({
                ...state,
                isOpen: false,
            })
        }
    }, [pathname])

    return {
        isOpen: state.isOpen,
        data: state.data,
        onOpen: (data) => {
            setState({
                isOpen: true,
                data,
            })
        },
        onClose: () => {
            setState({
                isOpen: false,
                data: null,
            })
        },
    }
}
